import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { WhaleSchema } from '@gilding/shared';
import { api, session } from '../api/client.js';

export function useWhales(enabled = true) {
  const address = session.read()?.split('.')[0] ?? '';
  const key = `gilding_pod_v1:${address}`;
  const client = useQueryClient();
  const cached = useMemo(() => {
  let cached: { whales: ReturnType<typeof WhaleSchema.parse>[]; at: number } | undefined;
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (saved && Date.now() - saved.at < 86400000 && Array.isArray(saved.whales)) {
      cached = { at: saved.at, whales: WhaleSchema.array().parse(saved.whales) };
    }
  } catch { /* Ignore old or incomplete browser snapshots. */ }
  return cached;
  }, [key]);
  return useQuery({
    queryKey: ['whales', address], enabled: enabled && !!address,
    queryFn: async () => {
      let data = await api.whales(false, undefined, true);
      const cursors = new Set<string>();
      while (data.nextCursor && cursors.size < 100) {
        if (cursors.has(data.nextCursor)) throw new Error('Wallet pagination stalled. Please retry.');
        cursors.add(data.nextCursor);
        client.setQueryData(['whales', address], data);
        const page = await api.whales(true, data.nextCursor, true);
        const merged = new Map([...data.whales, ...page.whales].map(w => [w.tokenId, w]));
        data = { whales: [...merged.values()].sort((a, b) => a.tokenId - b.tokenId), cached: false, nextCursor: page.nextCursor ?? null };
      }
      if (data.nextCursor) throw new Error('More whales remain. Please retry loading.');
      try { localStorage.setItem(key, JSON.stringify({ whales: data.whales, at: Date.now() })); } catch { /* storage is optional */ }
      return data;
    },
    ...(cached ? { initialData: { whales: cached.whales, cached: true }, initialDataUpdatedAt: cached.at } : {}),
    staleTime: 60000, gcTime: 1800000, retry: 2, retryDelay: attempt => Math.min(1000 * 2 ** attempt, 4000),
  });
}
